
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useResendConfirmation } from '@/hooks/useResendConfirmation';
import { toast } from 'sonner';
import { Mail, Lock, PawPrint, Zap, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@/services/analytics';

interface SignInFormProps {
  onShowResetPassword: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const inputBase = "w-full h-12 pl-10 pr-4 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-all";
const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1.5px solid rgba(255,193,7,0.45)',
} as React.CSSProperties;
const focusOn = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.border = '1.5px solid #FFC107';
  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,193,7,0.15)';
};
const focusOff = (e: React.FocusEvent<HTMLInputElement>) => {
  e.currentTarget.style.border = '1.5px solid rgba(255,193,7,0.45)';
  e.currentTarget.style.boxShadow = 'none';
};

const SignInForm: React.FC<SignInFormProps> = ({ onShowResetPassword, loading, setLoading }) => {
  const navigate = useNavigate();
  const { signIn, signInWithMagicLink } = useAuth();
  const { resendConfirmation } = useResendConfirmation();
  const { t } = useTranslation('auth');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowResendButton(false);
    setErrorMessage(null);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        if (error.code === 'email_not_confirmed' || error.message?.includes('email n\'est pas encore confirmé') || error.message?.includes('Email not confirmed')) {
          const msg = t('error_email_not_confirmed');
          setErrorMessage(msg);
          toast.error(msg);
          setShowResendButton(true);
        } else if (error.message?.includes('Invalid login credentials')) {
          const msg = t('error_wrong_credentials');
          setErrorMessage(msg);
          toast.error(msg);
        } else {
          setErrorMessage(error.message);
          toast.error(error.message);
        }
      } else {
        Analytics.loginCompleted('password');
        toast.success(t('success_signed_in'));
        navigate('/', { replace: true });
      }
    } catch {
      const msg = t('error_signin');
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    try {
      const { error } = await signInWithMagicLink(email);
      if (error) {
        setErrorMessage(error.message);
        toast.error(error.message);
      } else {
        setMagicSent(true);
      }
    } catch {
      toast.error(t('magic_error_send'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) { toast.error(t('error_no_email')); return; }
    setResendLoading(true);
    try {
      const { error } = await resendConfirmation(email);
      if (error) toast.error(t('error_resend_confirmation'));
      else { toast.success(t('success_resend_confirmation')); setShowResendButton(false); }
    } catch {
      toast.error(t('error_resend_confirmation'));
    } finally {
      setResendLoading(false);
    }
  };

  // ── Magic link envoyé ──
  if (magicSent) {
    return (
      <div className="space-y-4 text-center py-4">
        <div className="text-5xl">📧</div>
        <h3 className="font-bold text-lg text-foreground">{t('magic_check_inbox')}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('magic_sent_text', { email }).replace('<1>', '').replace('</1>', '')}
        </p>
        <button
          type="button"
          onClick={async () => {
            setResendLoading(true);
            await signInWithMagicLink(email);
            setResendLoading(false);
            toast.success(t('magic_link_resent'));
          }}
          disabled={resendLoading}
          className="w-full h-11 rounded-full text-sm font-medium text-foreground/80 border border-white/15 hover:bg-white/5 transition-all disabled:opacity-50"
        >
          {resendLoading ? t('magic_resending') : t('magic_resend')}
        </button>
        <button
          type="button"
          className="text-xs text-muted-foreground underline w-full text-center"
          onClick={() => { setMagicSent(false); setMagicMode(false); }}
        >
          {t('magic_back_to_signin')}
        </button>
      </div>
    );
  }

  // ── Mode magic link ──
  if (magicMode) {
    return (
      <form onSubmit={handleMagicSubmit} className="space-y-4 pt-1">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground/90">{t('email_label')}</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/70 pointer-events-none" />
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrorMessage(null); }}
              placeholder={t('email_placeholder')}
              required
              autoFocus
              className={inputBase}
              style={inputStyle}
              onFocus={focusOn}
              onBlur={focusOff}
            />
          </div>
        </div>

        {errorMessage && (
          <div
            className="rounded-xl px-4 py-3 text-sm font-medium text-center"
            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#FCA5A5' }}
          >
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !email}
          className="w-full rounded-full font-bold text-base text-black transition-all active:scale-95 disabled:opacity-60"
          style={{
            height: '52px',
            background: 'linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)',
            boxShadow: '0 4px 20px rgba(255,193,7,0.4)',
          }}
        >
          {loading ? t('magic_sending') : t('magic_link_button')}
        </button>

        <button
          type="button"
          onClick={() => { setMagicMode(false); setErrorMessage(null); }}
          className="w-full text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1.5 py-1"
        >
          {t('signin_back_to_password')}
        </button>
      </form>
    );
  }

  // ── Mode mot de passe (défaut) ──
  return (
    <form onSubmit={handlePasswordSubmit} className="space-y-4 pt-1">
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground/90">{t('email_label')}</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/70 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setShowResendButton(false); setErrorMessage(null); }}
            placeholder={t('email_placeholder')}
            required
            className={inputBase}
            style={inputStyle}
            onFocus={focusOn}
            onBlur={focusOff}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground/90">{t('password_label')}</label>
        <div className="relative">
          <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/70 pointer-events-none" />
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('password_placeholder')}
            required
            className={`${inputBase} pr-11`}
            style={inputStyle}
            onFocus={focusOn}
            onBlur={focusOff}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/70 hover:text-primary transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      {errorMessage && (
        <div
          className="rounded-xl px-4 py-3 text-sm font-medium text-center"
          style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#FCA5A5' }}
        >
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-full font-bold text-base text-black transition-all active:scale-95 disabled:opacity-60 mt-2"
        style={{
          height: '52px',
          background: 'linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)',
          boxShadow: '0 4px 20px rgba(255,193,7,0.4)',
        }}
      >
        {loading ? t('signin_loading') : t('signin_button')}
      </button>

      {showResendButton && (
        <button
          type="button"
          onClick={handleResendConfirmation}
          disabled={resendLoading}
          className="w-full h-11 rounded-full text-sm font-medium text-foreground/80 border border-white/15 hover:bg-white/5 transition-all"
        >
          {resendLoading ? t('magic_sending') : t('signin_resend_confirmation')}
        </button>
      )}

      <button
        type="button"
        onClick={() => { setMagicMode(true); setErrorMessage(null); setShowResendButton(false); }}
        className="w-full flex items-center justify-center gap-1.5 text-sm font-medium transition-colors py-1"
        style={{ color: '#C5A059' }}
      >
        <Zap size={14} />
        {t('signin_magic_button')}
      </button>

      <button
        type="button"
        onClick={onShowResetPassword}
        className="w-full text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1.5 py-0.5"
      >
        <PawPrint size={14} className="text-primary" />
        {t('signin_forgot_password')}
      </button>
    </form>
  );
};

export default SignInForm;
